import { useEffect, useState } from "react";
import axios from "axios";
import { useProjectCompletionConfirmation } from "@/components/ProjectCompletionDialog";
import { useProjectClosureConfirmation } from "@/components/ProjectClosureDialog";
import { Link } from "react-router-dom";
import { Send, ExternalLink, RefreshCw, Check, Shield, AlertTriangle, X, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { formatTimestamp } from "@/utils/datetime";
import BoardWorkspace from "@/components/BoardWorkspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-assistant`;

const PLACEHOLDER = "Find A02L7VJ · Note on A02L7VJ: crew delayed by rain · Move A02L7VJ to in progress";

const EXAMPLE_COMMANDS = [
  "Find A02L7VJ",
  "Show me A02L7DL",
  "Look up A02S45S and A02L7VJ",
  "Find A02",
];

const WRITE_LABEL = {
  roadblock_create: "Create roadblock",
  roadblock_clear: "Clear roadblock",
  lifecycle_change: "Change project stage",
  add_note: "Add project note",
};

const COMMAND_HELP = [
  { group: "Read", items: ["Find A02L7VJ", "Show me A02L7DL", "Look up A02S45S and A02L7VJ"] },
  { group: "Roadblock", items: ["Roadblock A02L7VJ waiting on locate", "Clear roadblock on A02L7VJ"] },
  { group: "Stage", items: ["Move A02L7VJ to in progress", "Move A02L7VJ to complete", "Move A02L7VJ to closed"] },
  { group: "Notes", items: ["Note on A02L7VJ: crew delayed by rain"] },
];

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState(() => {
    if (!expiresAt) return 0;
    return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  });
  useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return remaining;
}

function formatMMSS(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ActionAiAssistantPage() {
  const { hasPermission, isFeatureEnabled } = useAuth();
  const canUse = hasPermission("ai_assistant:use") && isFeatureEnabled("ai_assistant");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const { requestCompletion, completionDialog } = useProjectCompletionConfirmation();
  const { requestClosure, closureDialog, closureBusy } = useProjectClosureConfirmation(result?.write_preview?.preview_token);
  const [writeSuccess, setWriteSuccess] = useState(null);
  const [recent, setRecent] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);

  const previewExpiresAt = result?.intent === "write_preview" ? result?.write_preview?.expires_at : null;
  const previewSecondsRemaining = useCountdown(previewExpiresAt);
  const previewExpired = previewExpiresAt !== null && previewSecondsRemaining === 0;

  useEffect(() => {
    if (!canUse) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/recent`);
        if (!cancelled) setRecent(response.data.commands || []);
      } catch {
        if (!cancelled) setRecent([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUse]);

  const submit = async (event, override) => {
    if (event) event.preventDefault();
    const isPicker = override && typeof override === "object" && Array.isArray(override.resolved);
    const isWritePicker = override && typeof override === "object" && override.resolvedWriteProjectId;
    const value = (typeof override === "string" ? override : text).trim();
    if (!value && !isPicker && !isWritePicker) return;
    setBusy(true);
    setWriteSuccess(null);
    try {
      let payload;
      if (isPicker) {
        payload = { text: value || "resolve", resolved_project_ids: override.resolved };
      } else if (isWritePicker) {
        payload = { text: value, resolved_write_project_id: override.resolvedWriteProjectId };
      } else {
        payload = { text: value };
      }
      const response = await axios.post(`${API}/query`, payload);
      setResult(response.data);
      // Refresh the recent list (backend just prepended this command).
      if (!isPicker && !isWritePicker) {
        try {
          const rec = await axios.get(`${API}/recent`);
          setRecent(rec.data.commands || []);
        } catch {
          // silent
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Assistant could not answer that.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const resetForNextAsk = () => {
    setResult(null);
    setText("");
    setWriteSuccess(null);
  };

  const runExample = (command, extras) => {
    if (typeof command === "string") setText(command);
    submit(null, extras || command);
  };

  const confirmWrite = async (confirmed = false) => {
    const preview = result?.write_preview;
    const token = preview?.preview_token;
    if (!token) return;
    const closesProject = preview.params?.lifecycle_state === "closed";
    if (preview.params?.lifecycle_state === "complete" || closesProject) {
      if (new Date(preview.expires_at).getTime() <= Date.now()) {
        toast.error("Preview expired — ask again.");
        return;
      }
      if (confirmed !== true) {
        if (closesProject) {
          requestClosure({
            loadPreview: async () => {
              if (!preview.closure_review) throw new Error("Closure preview missing — ask again.");
              return preview.closure_review;
            },
            close: () => confirmWrite(true),
            onError: (error) => {
              // A stale signed preview must never be retried as a fresh approval.
              if (error?.response?.status === 409) setResult(null);
            },
          });
        } else {
          requestCompletion(() => confirmWrite(true));
        }
        return;
      }
    }
    setConfirming(true);
    try {
      const response = await axios.post(`${API}/execute`, { preview_token: token });
      setWriteSuccess(response.data);
      setResult(null);
      setText("");
      toast.success(response.data.message);
    } catch (err) {
      if (closesProject) throw err;
      const detail = err?.response?.data?.detail || "Could not apply that change.";
      toast.error(detail);
    } finally {
      setConfirming(false);
    }
  };

  const removeRecent = async (index) => {
    try {
      const response = await axios.delete(`${API}/recent/${index}`);
      setRecent(response.data.commands || []);
    } catch {
      // silent — the chip just won't disappear
    }
  };

  const clearAllRecent = async () => {
    try {
      await axios.delete(`${API}/recent`);
      setRecent([]);
    } catch {
      // silent
    }
  };

  if (!canUse) {
    return (
      <BoardWorkspace
        eyebrow="Action AI Assistant"
        headingTestId="action-ai-assistant-locked"
        subtitle="Ask the Platform Owner to enable this feature for your account."
        title="Not enabled"
      >
        <section className="project-notes-panel">The Action AI Assistant is not enabled for your account.</section>
      </BoardWorkspace>
    );
  }

  return (
    <BoardWorkspace
      eyebrow="Action AI Assistant · V2"
      headingTestId="action-ai-assistant-heading"
      subtitle={"Ask a read like \"Find A02L7VJ\", or a write like \"Note on A02L7VJ: crew delayed by rain\", \"Roadblock A02L7VJ waiting on locate\", or \"Move A02L7VJ to in progress\". Every write shows a preview and only runs when you confirm."}
      title="How can I help you?"
    >
      <form className="platform-owner-email-editor" onSubmit={submit} style={{ display: "grid", gap: "0.5rem" }}>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#374151" }}>
            Ask the Assistant
          </span>
          <textarea
            data-testid="action-ai-assistant-input"
            disabled={busy}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) submit(event);
            }}
            placeholder={PLACEHOLDER}
            rows={2}
            style={{
              border: "1px solid #cbd5e1",
              padding: "0.55rem 0.7rem",
              borderRadius: 4,
              fontSize: "0.9rem",
              resize: "vertical",
            }}
            value={text}
          />
        </label>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            aria-expanded={helpOpen}
            data-testid="action-ai-assistant-help-toggle"
            onClick={() => setHelpOpen((prev) => !prev)}
            style={{
              background: "transparent",
              border: "1px solid #d1d5db",
              color: "#374151",
              padding: "0.25rem 0.5rem",
              borderRadius: 4,
              fontSize: "0.75rem",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.25rem",
            }}
            type="button"
          >
            <HelpCircle size={13} /> {helpOpen ? "Hide commands" : "Show commands"}
          </button>
          <button
            data-testid="action-ai-assistant-submit"
            disabled={busy || !text.trim()}
            type="submit"
          >
            <Send size={14} />
            {busy ? "Working…" : "Ask"}
          </button>
        </div>
      </form>

      {helpOpen && (
        <section
          className="platform-owner-email-editor"
          data-testid="action-ai-assistant-help"
          style={{ marginTop: "0.5rem" }}
        >
          <strong style={{ fontSize: "0.85rem" }}>Supported commands</strong>
          <div style={{ display: "grid", gap: "0.5rem", marginTop: "0.4rem" }}>
            {COMMAND_HELP.map((section) => (
              <div key={section.group}>
                <div style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>
                  {section.group}
                </div>
                <ul style={{ margin: "0.2rem 0 0", padding: 0, listStyle: "none", display: "grid", gap: "0.2rem" }}>
                  {section.items.map((cmd) => (
                    <li
                      data-testid={`action-ai-assistant-help-item-${cmd.replace(/\s+/g, "-").toLowerCase()}`}
                      key={cmd}
                      style={{ fontSize: "0.8rem", color: "#374151", fontFamily: "monospace" }}
                    >
                      {cmd}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      <div
        data-testid="action-ai-assistant-examples"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.4rem",
          marginTop: "0.4rem",
          alignItems: "center",
        }}
      >
        {recent.length > 0 && (
          <>
            <span
              data-testid="action-ai-assistant-recent-label"
              style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}
            >
              Recent:
            </span>
            {recent.map((entry, index) => (
              <span
                data-testid={`action-ai-assistant-recent-chip-${index}`}
                key={`recent-${entry.at}-${index}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  border: "1px solid #93c5fd",
                  background: "#eff6ff",
                  borderRadius: 999,
                  color: "#1e3a8a",
                  fontSize: "0.75rem",
                  maxWidth: "22rem",
                }}
              >
                <button
                  data-testid={`action-ai-assistant-recent-${index}`}
                  disabled={busy}
                  onClick={() => runExample(entry.text)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "inherit",
                    padding: "0.25rem 0.25rem 0.25rem 0.6rem",
                    cursor: busy ? "not-allowed" : "pointer",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "18rem",
                  }}
                  title={entry.text}
                  type="button"
                >
                  {entry.text}
                </button>
                <button
                  aria-label={`Remove "${entry.text}" from recent`}
                  data-testid={`action-ai-assistant-recent-remove-${index}`}
                  onClick={() => removeRecent(index)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#1e3a8a",
                    cursor: "pointer",
                    padding: "0.15rem 0.5rem 0.15rem 0.1rem",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                  type="button"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <button
              data-testid="action-ai-assistant-recent-clear"
              onClick={clearAllRecent}
              style={{
                background: "transparent",
                border: "none",
                color: "#4b5563",
                cursor: "pointer",
                fontSize: "0.7rem",
                textDecoration: "underline",
                padding: 0,
              }}
              type="button"
            >
              Clear all
            </button>
          </>
        )}
        <span style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}>
          Try:
        </span>
        {EXAMPLE_COMMANDS.map((command) => (
          <button
            data-testid={`action-ai-assistant-example-${command.replace(/\s+/g, "-").toLowerCase()}`}
            disabled={busy}
            key={command}
            onClick={() => runExample(command)}
            style={{
              border: "1px solid #cbd5e1",
              background: "#f8fafc",
              padding: "0.25rem 0.6rem",
              borderRadius: 999,
              fontSize: "0.75rem",
              color: "#374151",
              cursor: busy ? "not-allowed" : "pointer",
            }}
            type="button"
          >
            {command}
          </button>
        ))}
      </div>

      {result?.intent === "unsupported" && (
        <section
          className="platform-dashboard-error"
          data-testid="action-ai-assistant-unsupported"
          role="alert"
        >
          {result.message}
        </section>
      )}

      {writeSuccess && (
        <section
          className="platform-owner-email-editor"
          data-testid="action-ai-assistant-write-success"
          style={{ borderLeft: "4px solid #16a34a", marginTop: "0.75rem" }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
            <Check aria-hidden="true" size={16} color="#16a34a" />
            <strong style={{ fontSize: "0.95rem" }}>{writeSuccess.message}</strong>
            <span style={{ marginLeft: "auto", color: "#4b5563", fontSize: "0.8rem" }}>
              {writeSuccess.project_code} · {writeSuccess.lifecycle_display}
              {writeSuccess.operational_blocker
                ? ` · Roadblocked${
                    writeSuccess.operational_blocker_reason
                      ? `: ${writeSuccess.operational_blocker_reason}`
                      : ""
                  }`
                : ""}
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
            <Link
              className="board-refresh-button"
              data-testid={`action-ai-assistant-open-record-${writeSuccess.project_id}`}
              to={`/projects/${writeSuccess.project_id}`}
            >
              <ExternalLink size={14} /> Open Project Record
            </Link>
            <button
              data-testid="action-ai-assistant-write-success-done"
              onClick={resetForNextAsk}
              type="button"
            >
              <RefreshCw size={14} /> Ask another
            </button>
          </div>
        </section>
      )}

      {result?.intent === "write_preview" && result?.write_preview && (
        <section
          className="platform-owner-email-editor"
          data-testid="action-ai-assistant-write-preview"
          style={{ borderLeft: "4px solid #b45309", marginTop: "0.75rem" }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
            <AlertTriangle aria-hidden="true" size={16} color="#b45309" />
            <span className="operations-kicker">
              {WRITE_LABEL[result.write_preview.command] || result.write_preview.command}
            </span>
            <strong style={{ fontSize: "0.95rem" }}>
              {result.write_preview.project_code}
            </strong>
            <span style={{ marginLeft: "auto", color: "#4b5563", fontSize: "0.8rem" }}>
              Currently {result.write_preview.current_lifecycle_display}
              {result.write_preview.current_operational_blocker
                ? ` · Roadblocked${
                    result.write_preview.current_operational_blocker_reason
                      ? `: ${result.write_preview.current_operational_blocker_reason}`
                      : ""
                  }`
                : ""}
            </span>
          </div>
          <p
            data-testid="action-ai-assistant-write-preview-summary"
            style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "#374151" }}
          >
            {result.write_preview.change_summary}
          </p>
          <p style={{ marginTop: "0.35rem", fontSize: "0.75rem", color: "#6b7280" }}>
            <Shield aria-hidden="true" size={12} style={{ verticalAlign: "-2px", marginRight: "0.25rem" }} />
            Nothing changes until you confirm.
            {" "}
            <span data-testid="action-ai-assistant-write-preview-countdown" style={{ color: previewExpired ? "#b91c1c" : "#6b7280", fontWeight: previewExpired ? 600 : 400 }}>
              {previewExpired ? "Preview expired — ask again." : `Expires in ${formatMMSS(previewSecondsRemaining)}.`}
            </span>
          </p>
          <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
            <button
              data-testid="action-ai-assistant-write-confirm"
              disabled={confirming || closureBusy || previewExpired}
              onClick={confirmWrite}
              style={{
                background: previewExpired ? "#9ca3af" : "#b45309",
                color: "#fff",
                border: "1px solid",
                borderColor: previewExpired ? "#9ca3af" : "#b45309",
                padding: "0.3rem 0.7rem",
                borderRadius: 4,
                cursor: confirming || closureBusy || previewExpired ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
              type="button"
            >
              <Check size={14} /> {confirming ? "Applying…" : "Confirm and apply"}
            </button>
            <button
              data-testid="action-ai-assistant-write-cancel"
              disabled={confirming}
              onClick={resetForNextAsk}
              type="button"
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {result?.intent === "write_ambiguous" && (
        <section
          className="platform-owner-email-editor"
          data-testid="action-ai-assistant-write-ambiguous"
          style={{ marginTop: "0.75rem" }}
        >
          <strong style={{ fontSize: "0.9rem" }}>{result.message || "Which project do you mean?"}</strong>
          <ul style={{ margin: "0.4rem 0 0", listStyle: "none", padding: 0, display: "grid", gap: "0.3rem" }}>
            {(result.ambiguous?.[0] || []).map((candidate) => (
              <li
                data-testid={`action-ai-assistant-write-ambiguous-candidate-${candidate.project_id}`}
                key={candidate.project_id}
                style={{
                  fontSize: "0.85rem",
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  <strong>{candidate.project_code}</strong> · {candidate.project_name} · {candidate.city} · {candidate.lifecycle_display}
                </span>
                <button
                  data-testid={`action-ai-assistant-write-ambiguous-pick-${candidate.project_id}`}
                  disabled={busy}
                  onClick={() => runExample(text, { resolvedWriteProjectId: candidate.project_id })}
                  style={{
                    background: "#111827",
                    color: "#fff",
                    border: "1px solid #111827",
                    padding: "0.25rem 0.55rem",
                    borderRadius: 4,
                    cursor: busy ? "not-allowed" : "pointer",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                  type="button"
                >
                  Pick
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(result?.cards?.length || result?.ambiguous?.length || result?.unmatched_terms?.length) > 0 && (
        <section
          className="platform-overview"
          data-testid="action-ai-assistant-results"
          style={{ gridTemplateColumns: "1fr" }}
        >
          {result.cards.map((card) => (
            <article
              className="platform-owner-email-editor"
              data-testid={`action-ai-assistant-card-${card.project_id}`}
              key={card.project_id}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                <span className="operations-kicker">{card.project_code}</span>
                <strong style={{ fontSize: "1rem" }}>{card.project_name}</strong>
                <span style={{ marginLeft: "auto", color: "#4b5563", fontSize: "0.8rem" }}>
                  {card.lifecycle_display}
                </span>
              </div>
              <dl style={{ marginTop: "0.35rem", fontSize: "0.8rem", color: "#374151" }}>
                {isFeatureEnabled("locates") && card.active_locate != null && <div><dt style={{ display: "inline", fontWeight: 500 }}>Locate: </dt><dd style={{ display: "inline", margin: 0 }}>{card.active_locate}</dd></div>}
                {isFeatureEnabled("permits") && card.active_permit != null && <div><dt style={{ display: "inline", fontWeight: 500 }}>Permit: </dt><dd style={{ display: "inline", margin: 0 }}>{card.active_permit}</dd></div>}
              </dl>
              {card.latest_notes.length > 0 && (
                <ul style={{ marginTop: "0.4rem", listStyle: "none", padding: 0, display: "grid", gap: "0.25rem", fontSize: "0.78rem" }}>
                  {card.latest_notes.map((note) => (
                    <li key={note.id} style={{ color: "#4b5563" }}>
                      <span style={{ color: "#6b7280", marginRight: "0.4rem" }}>{formatTimestamp(note.created_at)}</span>
                      {note.subject || note.body?.slice(0, 80) || "(note)"}
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                <Link
                  className="board-refresh-button"
                  data-testid={`action-ai-assistant-open-record-${card.project_id}`}
                  to={`/projects/${card.project_id}`}
                >
                  <ExternalLink size={14} /> Open Project Record
                </Link>
                <button
                  data-testid={`action-ai-assistant-ask-another-${card.project_id}`}
                  onClick={resetForNextAsk}
                  type="button"
                >
                  <RefreshCw size={14} /> Ask another
                </button>
                <button
                  data-testid={`action-ai-assistant-done-${card.project_id}`}
                  onClick={() => setResult(null)}
                  type="button"
                >
                  <Check size={14} /> Done
                </button>
              </div>
            </article>
          ))}

          {result.ambiguous.map((options, groupIndex) => (
            <article
              className="platform-owner-email-editor"
              data-testid={`action-ai-assistant-ambiguous-${groupIndex}`}
              key={`ambiguous-${groupIndex}`}
            >
              <strong style={{ fontSize: "0.9rem" }}>Choose which project</strong>
              <ul style={{ margin: "0.4rem 0 0", listStyle: "none", padding: 0, display: "grid", gap: "0.3rem" }}>
                {options.map((candidate) => (
                  <li
                    data-testid={`action-ai-assistant-candidate-${candidate.project_id}`}
                    key={candidate.project_id}
                    style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", fontSize: "0.85rem" }}
                  >
                    <span>
                      <strong>{candidate.project_code}</strong> · {candidate.project_name} · {candidate.city} · {candidate.lifecycle_display}{candidate.customer ? ` · ${candidate.customer}` : ""}
                    </span>
                    <button
                      onClick={() => submit(null, { resolved: [candidate.project_id] })}
                      type="button"
                    >
                      Pick
                    </button>
                  </li>
                ))}
              </ul>
            </article>
          ))}

          {result.unmatched_terms.length > 0 && (
            <article className="platform-owner-email-editor" data-testid="action-ai-assistant-unmatched">
              I couldn't find any project matching: {result.unmatched_terms.join(", ")}.
            </article>
          )}
        </section>
      )}

      {completionDialog}
      {closureDialog}
    </BoardWorkspace>
  );
}
